import { apiFetch } from "./apiClient";
import { readMinStayScopeStatus, type MinStayScopeStatus } from "./pricingApi";

export interface AutoSyncSchedule {
    connectionId: number;
    enabled: boolean;
    daysAhead: number;
    roomKindIds: string[];
    ratePlanId: string | null;
    /** Whether the scheduled run writes nightly prices. */
    sendPrices: boolean;
    /** Whether the scheduled run writes minimum-stay restrictions. */
    sendMinLos: boolean;
}

/**
 * One property's schedule as the auto-sync dialog reads it: the schedule, and
 * what the server says beside it of the property the read names.
 */
export interface NamedAutoSyncSchedule extends AutoSyncSchedule {
    /**
     * Whether Optivo holds a price plan for the property: `false` where no
     * price was ever calculated for it, so a schedule has nothing to send.
     *
     * Only a yes or a no is an answer. Absent from a server that predates the
     * field and from one that could not read the plan, and the dialog then
     * claims nothing about the property's prices.
     */
    hasPricePlan?: boolean;
}

export async function fetchAutoSyncSchedule(
    connectionId: number,
): Promise<NamedAutoSyncSchedule> {
    const res = await apiFetch(`/api/auto-sync/${connectionId}`);
    if (!res.ok) throw new Error(`fetchAutoSyncSchedule failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to fetch schedule");
    return typeof data.hasPricePlan === "boolean"
        ? { ...data.schedule, hasPricePlan: data.hasPricePlan }
        : data.schedule;
}

export async function updateAutoSyncSchedule(
    connectionId: number,
    payload: {
        enabled: boolean;
        daysAhead: number;
        roomKindIds: string[];
        ratePlanId: string | null;
        sendPrices?: boolean;
        sendMinLos?: boolean;
    },
): Promise<AutoSyncSchedule> {
    const res = await apiFetch(`/api/auto-sync/${connectionId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`updateAutoSyncSchedule failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to update schedule");
    return data.schedule;
}

/**
 * Whether the named property's PMS keeps one minimum stay per night for every
 * room type — a Previo hotel kept per season — in which case a schedule that
 * sends minimum stays sends each night the strictest of its room types.
 */
export async function fetchAutoSyncMinStayScope(connectionId: number): Promise<MinStayScopeStatus> {
    const res = await apiFetch(`/api/auto-sync/${connectionId}/min-stay-scope`);
    if (!res.ok) throw new Error(`fetchAutoSyncMinStayScope failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to read how the PMS keeps minimum stays");
    return readMinStayScopeStatus(data);
}

export async function fetchActiveAutoSyncSchedule(): Promise<AutoSyncSchedule> {
    const res = await apiFetch("/api/auto-sync/active");
    if (!res.ok) throw new Error(`fetchActiveAutoSyncSchedule failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to fetch schedule");
    return data.schedule;
}
